import { useT } from '@transifex/react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { AccessibilityInfo } from 'react-native';

import type {
  RepeatingListColumn,
  RepeatingListRow,
  RepeatingListRowState,
} from '../types/assessmentQuestion';
import { cellErrorMessage } from '../utils/repeatingListCell';
import type { ActiveQuestion } from '../utils/resolveQuestionChoices';

export type RepeatingListRowsApi = {
  getRows: (key: string) => RepeatingListRowState[];
  /** ARIA live-region text for this question — see `announce` below. */
  getStatusMessage: (key: string) => string;
  /** Replace one question's rows outright (used to apply a validation patch). */
  setRows: (key: string, next: RepeatingListRowState[]) => void;
  /** Give a repeatingList question its starting rows. Idempotent per key. */
  seedRows: (question: ActiveQuestion) => void;
  addRow: (key: string, columns: RepeatingListColumn[]) => void;
  removeRow: (key: string, rowId: string) => void;
  updateRowValue: (key: string, rowId: string, columnName: string, text: string) => void;
  validateRowCell: (key: string, rowId: string, column: RepeatingListColumn) => void;
};

/**
 * The `repeatingList` half of a question's pending state: every question's
 * rows, keyed by question key, plus the add/remove/edit/validate mutations and
 * the screen-reader announcements they need.
 *
 * Split out of `useAssessmentPendingState` because rows are the one field with
 * mechanics of their own — a synchronously-current ref mirror (see `rowsRef`),
 * stable row ids, and an accessibility announcement per mutation — none of
 * which the plain value fields need.
 *
 * `onRowEdited` fires whenever a cell is typed into, so the caller can clear
 * that question's required-error banner the same way typing into any other
 * field does.
 */
export function useRepeatingListRows({
  onRowEdited,
}: {
  onRowEdited: (key: string) => void;
}): RepeatingListRowsApi {
  const t = useT();
  const [rowsByKey, setRowsByKey] = useState<Record<string, RepeatingListRowState[]>>({});
  const [statusMessageByKey, setStatusMessageByKey] = useState<Record<string, string>>({});

  // Mirrors `rowsByKey`, written synchronously by every row mutator (`addRow`,
  // `removeRow`, `updateRowValue`, `validateRowCell`) at the same moment as the
  // matching `setRowsByKey` call. `rowsByKey` itself only reflects the LAST
  // render React committed, so two mutations dispatched before that commit
  // happens — a rapid double-tap or multi-touch batched into one React update —
  // would both read the SAME stale array off state: `removeRow`'s `length <= 1`
  // floor could then pass twice and empty the list despite it, and `addRow`'s
  // announced row number could repeat. Reading/writing this ref instead makes
  // every mutation see the true, up-to-the-instant row list regardless of
  // what has or hasn't rendered yet.
  const rowsRef = useRef<Record<string, RepeatingListRowState[]>>({});
  // Per-key toggle for the ARIA live region text — see `announce` below.
  const flipRef = useRef<Record<string, boolean>>({});
  const nextRowId = useRef(0);

  const getRows = useCallback((key: string) => rowsByKey[key] ?? [], [rowsByKey]);

  const getStatusMessage = useCallback(
    (key: string) => statusMessageByKey[key] ?? '',
    [statusMessageByKey]
  );

  const setRows = useCallback((key: string, next: RepeatingListRowState[]) => {
    rowsRef.current = { ...rowsRef.current, [key]: next };
    setRowsByKey((prev) => ({ ...prev, [key]: next }));
  }, []);

  const createRow = useCallback((columns: RepeatingListColumn[]): RepeatingListRowState => {
    const id = `row-${nextRowId.current}`;
    nextRowId.current += 1;
    return {
      id,
      values: Object.fromEntries(columns.map((column) => [column.name, ''])),
      errors: {},
    };
  }, []);

  // A prefilled starting row: same shell as `createRow` (fresh id, empty
  // `errors`), but seeded with server-supplied values instead of blanks.
  // Spreads `values` over a FULL blank column map (not just the keys the
  // server sent) so every column's `TextField` gets a defined `''` even when
  // a row's prefilled data doesn't cover every column — a controlled
  // TextField fed `undefined` would warn/misbehave. Only copies a value for a
  // column this question actually has: `toInitialRows` already dropped
  // unknown column names at the source, but re-checking here means this
  // helper is safe to feed un-sanitized values too (e.g. directly from a
  // test), not just the converter's output. `hasOwnProperty` rather than `in`
  // is defensive habit, not a real attack surface: `row.values` is built from
  // the authored column names, so `in` would only differ for a column
  // literally authored as `toString`/`constructor` — where matching would be
  // correct anyway.
  const createRowFromPrefilled = useCallback(
    (columns: RepeatingListColumn[], values: RepeatingListRow): RepeatingListRowState => {
      const row = createRow(columns);
      const knownValues = Object.fromEntries(
        Object.entries(values).filter(([name]) =>
          Object.prototype.hasOwnProperty.call(row.values, name)
        )
      );
      return { ...row, values: { ...row.values, ...knownValues } };
    },
    [createRow]
  );

  // Adding or removing a row changes the screen without moving focus, so a
  // screen reader is told nothing on its own — see the ARIA live region in
  // `RepeatingListField` for the web half; `announceForAccessibility` is the
  // native (iOS/Android) half, and is inert (empty function) in
  // react-native-web, which is why both are always called. `flipRef` toggles a
  // trailing zero-width space on/off per KEY so two consecutive identical
  // messages for the SAME repeating-list question still change the rendered
  // string (React's same-value `setState` bailout would otherwise swallow the
  // second one on web).
  const announce = useCallback((key: string, message: string) => {
    const flipped = !(flipRef.current[key] ?? false);
    flipRef.current = { ...flipRef.current, [key]: flipped };
    setStatusMessageByKey((prev) => ({ ...prev, [key]: flipped ? `${message}\u200B` : message }));
    AccessibilityInfo.announceForAccessibility(message);
  }, []);

  /**
   * Seeds a fresh repeatingList question's starting rows. Reads `rowsRef`, not
   * `rowsByKey` state — state only reflects the LAST render React committed
   * (see `rowsRef` above), so a second call before a prior seeding
   * `setRowsByKey` commits would otherwise see the key as still-unseeded and
   * stomp whatever was already typed with fresh empty rows (this is also
   * what keeps a mid-typing re-render from re-seeding and overwriting
   * member-typed values — the key is already present in `rowsRef` by then).
   * Non-repeatingList questions are a no-op, so a caller can hand over any
   * question.
   *
   * When the question carries server-supplied `initialRows` (see
   * `AssessmentQuestion.initialRows`), one row is built per entry via
   * `createRowFromPrefilled` instead of blank rows, and `initialRowCount` is
   * ignored — an absent/empty `initialRows` falls back to that count of blank
   * rows exactly as before this seeded-from-the-server pathway existed.
   */
  const seedRows = useCallback(
    (question: ActiveQuestion) => {
      if (question.questionType !== 'repeatingList') return;
      if (rowsRef.current[question.key] !== undefined) return;
      const columns = question.columns ?? [];
      const { initialRows } = question;
      const rows =
        initialRows && initialRows.length > 0
          ? initialRows.map((values) => createRowFromPrefilled(columns, values))
          : Array.from({ length: question.initialRowCount ?? 1 }, () => createRow(columns));
      setRows(question.key, rows);
    },
    [createRow, createRowFromPrefilled, setRows]
  );

  // No upper bound — there's no `maxRowCount` on the wire today (see
  // AssessmentQuestion.initialRowCount's doc comment). Reads/writes through
  // `rowsRef` rather than `rowsByKey` state — see rowsRef's comment.
  const addRow = useCallback(
    (key: string, columns: RepeatingListColumn[]) => {
      const next = [...(rowsRef.current[key] ?? []), createRow(columns)];
      setRows(key, next);
      announce(key, t('Item {number} added', { number: next.length }));
    },
    [createRow, setRows, announce, t]
  );

  // Floors at 1 row — see RepeatingListRowState's doc comment for why a
  // required question can never be left with zero fields to answer.
  const removeRow = useCallback(
    (key: string, rowId: string) => {
      const prev = rowsRef.current[key] ?? [];
      if (prev.length <= 1) return;
      const removedIndex = prev.findIndex((row) => row.id === rowId);
      if (removedIndex === -1) return;
      setRows(
        key,
        prev.filter((row) => row.id !== rowId)
      );
      announce(key, t('Item {number} removed', { number: removedIndex + 1 }));
    },
    [setRows, announce, t]
  );

  // Update one cell of one row. Matched by `row.id`, not position, so it stays
  // correct after an earlier row is removed and the rest shift up. Clears
  // that cell's own error optimistically on every keystroke, so it's
  // re-validated on blur/Continue rather than showing a stale message.
  const updateRowValue = useCallback(
    (key: string, rowId: string, columnName: string, text: string) => {
      onRowEdited(key);
      const current = rowsRef.current[key] ?? [];
      setRows(
        key,
        current.map((row) =>
          row.id === rowId
            ? {
                ...row,
                values: { ...row.values, [columnName]: text },
                errors: { ...row.errors, [columnName]: undefined },
              }
            : row
        )
      );
    },
    [onRowEdited, setRows]
  );

  // Surface one cell's error as soon as it loses focus, same as the standalone
  // date question's blur check.
  const validateRowCell = useCallback(
    (key: string, rowId: string, column: RepeatingListColumn) => {
      const current = rowsRef.current[key] ?? [];
      setRows(
        key,
        current.map((row) =>
          row.id === rowId
            ? {
                ...row,
                errors: {
                  ...row.errors,
                  [column.name]: cellErrorMessage(column, row.values[column.name] ?? '', t),
                },
              }
            : row
        )
      );
    },
    [setRows, t]
  );

  return useMemo(
    () => ({
      getRows,
      getStatusMessage,
      setRows,
      seedRows,
      addRow,
      removeRow,
      updateRowValue,
      validateRowCell,
    }),
    [
      getRows,
      getStatusMessage,
      setRows,
      seedRows,
      addRow,
      removeRow,
      updateRowValue,
      validateRowCell,
    ]
  );
}
